import {
  anunciarSessaoExpirada,
  anunciarSessaoValida,
  ehDesvioDeLogin,
} from './sessao.js';
import {
  API_PREFIX,
  type ActivePomodoroResponse,
  type ApiErrorBody,
  type Countdown,
  type CreateCountdownPayload,
  type CreateHabitPayload,
  type CreateListPayload,
  type CreateTagPayload,
  type CreateTaskPayload,
  type EndPomodoroPayload,
  type Habit,
  type HabitLog,
  type HabitStatsResponse,
  type ImportResponse,
  type List,
  type LogHabitPayload,
  type MeResponse,
  type PomodoroSession,
  type PomodoroStats,
  type SnoozePreset,
  type StartPomodoroPayload,
  type Tag,
  type Task,
  type UpdateCountdownPayload,
  type UpdateHabitPayload,
  type UpdateListPayload,
  type UpdateTagPayload,
  type UpdateTaskPayload,
  type User,
  type UserSettingsInput,
} from '@cleartask/shared';

/**
 * Erro de API com o codigo do servidor preservado, para a UI decidir o que
 * mostrar sem parsear mensagem de texto.
 */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Em desenvolvimento nao existe Cloudflare Access na frente do wrangler dev,
 * entao o app se identifica por header. Em producao este valor e vazio e o
 * navegador envia o cookie CF_Authorization sozinho.
 */
const DEV_EMAIL = import.meta.env['VITE_DEV_EMAIL'] as string | undefined;

/**
 * A sessao do Access caiu no meio do caminho.
 *
 * NAO estende `ApiClientError` de proposito: `ehFalhaDeRede` (lib/sync.ts) trata
 * tudo que nao e `ApiClientError` como falha de rede, e e exatamente o que se
 * quer aqui — a escrita vai para a fila e drena quando a sessao voltar. O que
 * muda e a tela deixar de chamar isso de "sem conexao".
 */
export class SessaoExpiradaError extends Error {
  constructor() {
    super('A sessao expirou. Entre de novo para continuar.');
    this.name = 'SessaoExpiradaError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (DEV_EMAIL) headers.set('X-Dev-Email', DEV_EMAIL);
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');

  const res = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    headers,
    credentials: 'include',
    // `manual` para PODER ver o desvio. Com o padrao (`follow`), o 302 do
    // Access para o login noutro dominio vira erro de CORS — indistinguivel de
    // queda de rede, que e como este defeito passou despercebido.
    redirect: 'manual',
  });

  if (ehDesvioDeLogin(res)) {
    anunciarSessaoExpirada();
    throw new SessaoExpiradaError();
  }

  // Chegou resposta de verdade: se o aviso estava aceso, apaga.
  anunciarSessaoValida();

  if (!res.ok) {
    let code = 'http_error';
    let message = `A API respondeu ${res.status}.`;
    try {
      const errorBody = (await res.json()) as ApiErrorBody;
      if (errorBody.error) {
        code = errorBody.error.code;
        message = errorBody.error.message;
      }
    } catch {
      // Resposta sem corpo JSON — mantem a mensagem generica acima.
    }
    throw new ApiClientError(res.status, code, message);
  }

  // 204 (DELETE de lista) nao tem corpo; chamar res.json() aqui lancaria um
  // erro de parse justamente no caminho de sucesso.
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

function json(payload: unknown): string {
  return JSON.stringify(payload);
}

export type TaskView = 'today' | 'upcoming' | 'matrix' | 'list' | 'trash';

export interface TaskListQuery {
  view: TaskView;
  listId?: string;
  /** Filtra pelas tarefas que carregam esta tag. */
  tagId?: string;
  /** Filhas desta tarefa; `'null'` pede so as de primeiro nivel. */
  parentId?: string;
  since?: number;
  includeDone?: boolean;
  /** Janela de vencimento (epoch ms), usada pelo calendario. Bordas inclusivas. */
  from?: number;
  to?: number;
  /** Busca textual em titulo e notas (spec 10.2). */
  q?: string;
  /**
   * Teto de linhas. Ausente, o servidor aplica `TASK_PAGE_SIZE` — a protecao
   * nao depende de o cliente lembrar dela.
   */
  limit?: number;
  /**
   * Recorte das concluidas, em dias. So as telas que MOSTRAM historico pedem:
   * a busca e o calendario precisam enxergar qualquer epoca.
   */
  doneDays?: number;
}

export interface TaskListResponse {
  tasks: Task[];
  /** Relogio do SERVIDOR — ancora do proximo delta sync, nao o do dispositivo. */
  now: number;
  /**
   * O teto cortou a resposta.
   *
   * Sem este sinal a tela nao distinguiria "acabou" de "parou aqui", e uma
   * lista incompleta com cara de completa e pior que uma lista sem teto.
   */
  hasMore?: boolean;
}

export interface TaskDayStat {
  /** Dia civil no fuso do usuario, 'AAAA-MM-DD'. */
  date: string;
  criadas: number;
  concluidas: number;
}

export interface TaskStatsResponse {
  stats: {
    from: number;
    to: number;
    /** Dia civil do SERVIDOR — quem decide o que e "hoje" nao e o aparelho. */
    today: string;
    byDay: TaskDayStat[];
    totalCriadas: number;
    totalConcluidas: number;
    abertas: number;
    vencidas: number;
  };
  range: 'week' | 'month';
}

export interface CompleteTaskResponse {
  task: Task;
  /** Proxima instancia, quando a tarefa tem recorrencia. */
  next: Task | null;
}

function taskQueryString(query: TaskListQuery): string {
  const params = new URLSearchParams({ view: query.view });
  if (query.listId) params.set('listId', query.listId);
  if (query.tagId) params.set('tagId', query.tagId);
  if (query.parentId) params.set('parentId', query.parentId);
  if (query.since !== undefined) params.set('since', String(query.since));
  if (query.includeDone) params.set('includeDone', 'true');
  if (query.from !== undefined) params.set('from', String(query.from));
  if (query.to !== undefined) params.set('to', String(query.to));
  if (query.q) params.set('q', query.q);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.doneDays !== undefined) params.set('doneDays', String(query.doneDays));
  return params.toString();
}

export const api = {
  me: () => request<MeResponse>('/me'),

  settings: {
    update: (patch: Partial<UserSettingsInput>) =>
      request<{ user: User }>('/me/settings', { method: 'PATCH', body: json(patch) }),
  },

  /**
   * Export (spec 5).
   *
   * Devolve o Blob cru, e nao JSON: o corpo pode ser CSV, e o objetivo e
   * entrega-lo ao navegador para download — parsear no meio do caminho so para
   * reserializar seria trabalho jogado fora.
   */
  exportData: async (format: 'json' | 'csv'): Promise<Blob> => {
    const headers = new Headers();
    if (DEV_EMAIL) headers.set('X-Dev-Email', DEV_EMAIL);

    const res = await fetch(`${API_PREFIX}/export?format=${format}`, {
      headers,
      credentials: 'include',
    });

    if (!res.ok) {
      throw new ApiClientError(res.status, 'export_failed', 'Não foi possível exportar.');
    }
    return res.blob();
  },

  /**
   * Trilha de auditoria, 50 por pagina. `before` e o cursor devolvido pela
   * pagina anterior; `all` so vale para admin (membro recebe 403).
   */
  audit: (opts: { before?: number; entity?: string; all?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.before !== undefined) q.set('before', String(opts.before));
    if (opts.entity) q.set('entity', opts.entity);
    if (opts.all) q.set('all', '1');
    const qs = q.toString();
    return request<import('../features/audit/types.js').AuditPage>(
      `/audit${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * Import do proprio backup (PROXIMOS_PASSOS 3.4).
   *
   * Recebe o TEXTO do arquivo, sem parsear: quem valida e o servidor, e
   * parsear aqui so para reserializar trocaria um erro legivel da API ("formato
   * 1, o import le o 2") por uma excecao generica do `JSON.parse` no navegador.
   */
  importData: (texto: string, dryRun: boolean) =>
    request<ImportResponse>(`/import${dryRun ? '?dryRun=1' : ''}`, {
      method: 'POST',
      body: texto,
    }),

  tasks: {
    /** Conclusoes e criacoes por dia, mais o retrato de pendencias. */
    stats: (range: 'week' | 'month') =>
      request<TaskStatsResponse>(`/tasks/stats?range=${range}`),

    list: (query: TaskListQuery) =>
      request<TaskListResponse>(`/tasks?${taskQueryString(query)}`),

    /** Uma tarefa pelo id — o que faz `/tarefas/:id` funcionar num link colado. */
    byId: (id: string) => request<{ task: Task }>(`/tasks/${id}`),

    create: (input: CreateTaskPayload) =>
      request<{ task: Task }>('/tasks', { method: 'POST', body: json(input) }),

    update: (id: string, patch: UpdateTaskPayload) =>
      request<{ task: Task }>(`/tasks/${id}`, { method: 'PATCH', body: json(patch) }),

    complete: (id: string) =>
      request<CompleteTaskResponse>(`/tasks/${id}/complete`, { method: 'POST' }),

    snooze: (id: string, payload: { preset: SnoozePreset } | { dueAt: number }) =>
      request<{ task: Task }>(`/tasks/${id}/snooze`, {
        method: 'POST',
        body: json(payload),
      }),

    remove: (id: string) => request<{ task: Task }>(`/tasks/${id}`, { method: 'DELETE' }),

    restore: (id: string) =>
      request<{ task: Task }>(`/tasks/${id}/restore`, { method: 'POST' }),

    reorder: (items: Array<{ id: string; sortOrder: number }>) =>
      request<{ updated: number; now: number }>('/tasks/reorder', {
        method: 'POST',
        body: json({ items }),
      }),
  },

  pomodoro: {
    active: () => request<ActivePomodoroResponse>('/pomodoro/sessions/active'),

    start: (input: StartPomodoroPayload) =>
      request<{ session: PomodoroSession }>('/pomodoro/sessions', {
        method: 'POST',
        body: json(input),
      }),

    end: (id: string, input: EndPomodoroPayload) =>
      request<{ session: PomodoroSession }>(`/pomodoro/sessions/${id}`, {
        method: 'PATCH',
        body: json(input),
      }),

    stats: (range: 'week' | 'month') =>
      request<{ stats: PomodoroStats; now: number }>(`/pomodoro/stats?range=${range}`),
  },

  tags: {
    list: () => request<{ tags: Tag[] }>('/tags'),

    create: (input: CreateTagPayload) =>
      request<{ tag: Tag }>('/tags', { method: 'POST', body: json(input) }),

    update: (id: string, patch: UpdateTagPayload) =>
      request<{ tag: Tag }>(`/tags/${id}`, { method: 'PATCH', body: json(patch) }),

    /**
     * Devolve a tag removida E quantas tarefas perderam o rotulo.
     *
     * A contagem nao e enfeite: apagar uma tag age em tarefas que nao estao na
     * tela, e um toast dizendo so "Excluída" esconderia o alcance da acao.
     */
    remove: (id: string) =>
      request<{ tag: Tag; affectedTasks: number }>(`/tags/${id}`, { method: 'DELETE' }),
  },

  lists: {
    list: (includeArchived = false) =>
      request<{ lists: List[] }>(`/lists${includeArchived ? '?includeArchived=true' : ''}`),

    create: (input: CreateListPayload) =>
      request<{ list: List }>('/lists', { method: 'POST', body: json(input) }),

    update: (id: string, patch: UpdateListPayload) =>
      request<{ list: List }>(`/lists/${id}`, { method: 'PATCH', body: json(patch) }),

    remove: (id: string) => request<void>(`/lists/${id}`, { method: 'DELETE' }),
  },

  habits: {
    list: (includeArchived = false) =>
      request<{ habits: Habit[] }>(
        `/habits${includeArchived ? '?includeArchived=true' : ''}`,
      ),

    create: (input: CreateHabitPayload) =>
      request<{ habit: Habit }>('/habits', { method: 'POST', body: json(input) }),

    update: (id: string, patch: UpdateHabitPayload) =>
      request<{ habit: Habit }>(`/habits/${id}`, { method: 'PATCH', body: json(patch) }),

    remove: (id: string) => request<void>(`/habits/${id}`, { method: 'DELETE' }),

    /** Upsert do dia. Devolve tambem o dia civil do servidor. */
    log: (id: string, input: LogHabitPayload) =>
      request<{ log: HabitLog; today: string }>(`/habits/${id}/log`, {
        method: 'POST',
        body: json(input),
      }),

    /** Sem janela, o servidor devolve os sete dias que terminam hoje. */
    stats: (window?: { from: string; to: string }) => {
      const query = window ? `?from=${window.from}&to=${window.to}` : '';
      return request<HabitStatsResponse>(`/habits/stats${query}`);
    },
  },

  countdowns: {
    list: () => request<{ countdowns: Countdown[] }>('/countdowns'),

    create: (input: CreateCountdownPayload) =>
      request<{ countdown: Countdown }>('/countdowns', {
        method: 'POST',
        body: json(input),
      }),

    update: (id: string, patch: UpdateCountdownPayload) =>
      request<{ countdown: Countdown }>(`/countdowns/${id}`, {
        method: 'PATCH',
        body: json(patch),
      }),

    // Devolve o registro removido — e com ele que o desfazer recria a contagem.
    remove: (id: string) =>
      request<{ countdown: Countdown }>(`/countdowns/${id}`, { method: 'DELETE' }),
  },

  push: {
    /**
     * Chave publica VAPID do ambiente.
     *
     * Vem do servidor em vez de embutida no bundle: o mesmo arquivo servido
     * contra outro Worker se inscreveria com a chave errada, e o sintoma seria
     * uma notificacao que nunca chega — sem erro em lugar nenhum.
     * `null` significa "este ambiente nao tem push configurado".
     */
    key: () => request<{ publicKey: string | null }>('/push/key'),

    subscribe: (input: {
      id: string;
      endpoint: string;
      p256dh: string;
      auth: string;
      userAgent: string | null;
    }) => request<{ subscription: { id: string } }>('/push/subscribe', {
      method: 'POST',
      body: json(input),
    }),

    unsubscribe: (endpoint: string) =>
      request<void>('/push/subscribe', { method: 'DELETE', body: json({ endpoint }) }),
  },
};
